import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, ClipboardCheck, FileText } from "lucide-react";
import { ARTICLES } from "@/data/content";
import { useSEO } from "@/lib/seo";
import { trackResourceClick } from "@/lib/analytics";
import CTASection from "@/components/CTASection";

const GUIDE_LINKS = [
  { icon: FileText, title: "Tier II Reporting Guide", slug: "what-is-tier-ii-reporting", desc: "Understand what Tier II reporting is and how it works." },
  { icon: BookOpen, title: "EPCRA Guides", slug: "epcra-311-vs-312", desc: "Learn the difference between EPCRA Sections 311 and 312." },
  { icon: ClipboardCheck, title: "Chemical Inventory Checklist", slug: "tier-ii-chemical-inventory-checklist", desc: "A practical checklist to organize your inventory." },
];

export default function Resources() {
  const [filter, setFilter] = useState("All");
  const categories = ["All", ...Array.from(new Set(ARTICLES.map((a) => a.category)))];
  const list = filter === "All" ? ARTICLES : ARTICLES.filter((a) => a.category === filter);

  useSEO({
    title: "Resources — Tier II & EPCRA Guides",
    description:
      "Tier II reporting guides, EPCRA articles, chemical inventory checklists and SDS resources to help U.S. facilities prepare accurate environmental compliance reports.",
    path: "/resources",
  });

  return (
    <>
      <section className="relative overflow-hidden bg-navy-900 noise-overlay">
        <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-16 lg:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Resources</p>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            Tier II & EPCRA knowledge center
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-300">
            Useful, plain-language guidance on chemical inventory reporting — written for EHS teams, not search engines.
          </p>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {GUIDE_LINKS.map((g) => {
              const Icon = g.icon;
              return (
                <Link
                  key={g.slug}
                  to={`/resources/${g.slug}`}
                  onClick={() => trackResourceClick(g.slug, "resources-guide")}
                  className="group flex items-start gap-4 rounded-xl border border-slate-200 bg-slate-50 p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
                  data-testid={`guide-${g.slug}`}
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-navy-900 text-amber-500">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-navy-900 group-hover:text-amber-600 transition-colors">{g.title}</h3>
                    <p className="mt-1 text-sm text-slate-600">{g.desc}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-8" data-testid="resources-filters">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                  filter === c ? "bg-navy-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-slate-400"
                }`}
                data-testid={`filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map((a) => (
              <Link
                key={a.slug}
                to={`/resources/${a.slug}`}
                onClick={() => trackResourceClick(a.slug, "resources-article")}
                className="group flex flex-col rounded-xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                data-testid={`article-card-${a.slug}`}
              >
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-600">{a.category}</span>
                <h2 className="mt-3 font-heading text-lg font-bold text-navy-900 group-hover:text-amber-600 transition-colors">{a.title}</h2>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{a.excerpt}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500">{a.read}</span>
                  <ArrowRight className="h-4 w-4 text-navy-900 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CTASection location="resources-footer" />
    </>
  );
}
